// Plan 4a, Tasks 6–7: the Consultor workspace (subscriptions, subrogation, partner credit and Resgatar), consultant
// invites, and the team (members of a customer).
import { z } from 'zod';
import { Id } from './accounts.ts';

const UserId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
const Email = z.string().trim().toLowerCase().email().max(254);

// --- Consultor ------------------------------------------------------------------------------------------------------

export const ConsultorItem = z.object({
  subscriptionId: Id,
  anmEntityId: Id,
  anmEntityLabel: z.string(),
  /** `holder`: subrogated to this customer — the holder pays; `you`: this customer's own subscription. */
  payer: z.enum(['holder', 'you']),
  /** The paying customer's display name (S-p). */
  holderName: z.string(),
  status: z.enum(['pending', 'trial', 'active', 'cancelled']),
  tierCode: z.string().nullable(),
  modules: z.array(z.string()),
  processCount: z.number().int().nonnegative().nullable(),
  nextDue: z.object({ dueOn: z.string(), daysLeft: z.number().int(), kind: z.string() }).nullable(),
  /** Own subscriptions only: who it is subrogated to now, or null. */
  subrogee: z.object({ customerId: Id, label: z.string() }).nullable(),
  warning: z.string().nullable(),
});
export type ConsultorItem = z.infer<typeof ConsultorItem>;

export const ConsultorResponse = z.object({
  stats: z.object({
    members: z.number().int().nonnegative(),
    entities: z.number().int().nonnegative(),
    subrogatedToYou: z.number().int().nonnegative(),
    youSubscribe: z.number().int().nonnegative(),
  }),
  items: z.array(ConsultorItem),
});
export type ConsultorResponse = z.infer<typeof ConsultorResponse>;

// --- partner credit and Resgatar ------------------------------------------------------------------------------------

export const PixKeyType = z.enum(['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP']);

export const WithdrawalItem = z.object({
  id: Id,
  amountCents: z.number().int().positive(),
  pixKeyType: PixKeyType,
  status: z.enum(['awaiting_document', 'approved', 'paid', 'failed', 'rejected']),
  requestedAt: z.string(),
});
export type WithdrawalItem = z.infer<typeof WithdrawalItem>;

export const PartnerResponse = z.object({
  isConsultant: z.boolean(),
  /** null: never applied. */
  partner: z.object({ status: z.enum(['pending', 'approved', 'rejected', 'suspended']), pixRedemption: z.boolean() }).nullable(),
  /** Partner credit only — top-up credit is never withdrawn self-service (S-t). */
  credit: z.object({ availableCents: z.number().int(), pendingCents: z.number().int() }),
  withdrawals: z.array(WithdrawalItem),
});
export type PartnerResponse = z.infer<typeof PartnerResponse>;

export const WithdrawalBody = z.object({
  amountCents: z.number().int().positive(),
  pixKeyType: PixKeyType,
  pixKey: z.string().trim().min(1).max(140),
});

// --- invites --------------------------------------------------------------------------------------------------------

export const InviteState = z.enum(['open', 'accepted', 'used', 'revoked', 'expired']);

/** A consultant's client invite: e-mailed when `email` is given; the link can always be copied. */
/** A consultoria's client invite names the titular to monitor; no CNPJ (it binds to no document — Plan 4a.1). */
export const ClientInviteBody = z.object({ email: Email.optional(), anmEntityId: Id });
export const InviteCreated = z.object({ inviteId: Id, url: z.string(), expiresAt: z.string() });
export type InviteCreated = z.infer<typeof InviteCreated>;

export const InviteItem = z.object({
  id: Id,
  email: z.string().nullable(),
  state: InviteState,
  createdAt: z.string(),
  expiresAt: z.string(),
  /** Only while open: the link to copy again. */
  url: z.string().nullable(),
  /** A client invite: the titular to monitor (Plan 4a.1). */
  titularLabel: z.string().nullable(),
});
export type InviteItem = z.infer<typeof InviteItem>;
export const InvitesResponse = z.object({ invites: z.array(InviteItem) });
export type InvitesResponse = z.infer<typeof InvitesResponse>;
export const InviteParams = z.object({ inviteId: Id });
export const InviteTokenParams = z.object({ token: z.string().min(3).max(200) });

/** What a link is, for its landing page (public: names the inviter only). */
export const InviteInfo = z.object({
  /** client: a consultoria's client link; member: join a team; consultant (S-z): a customer shares one subscription */
  kind: z.enum(['client', 'member', 'consultant']),
  inviterLabel: z.string(),
  /** consultant: the ANM entity whose monitoring is shared */
  subjectLabel: z.string().nullable(),
  role: z.enum(['owner', 'member']).nullable(),
  state: InviteState,
  expiresAt: z.string(),
});
export type InviteInfo = z.infer<typeof InviteInfo>;

/** `customerId`: for a consultant link, the consultoria (one the login acts for) that takes the shared subscription. */
export const InviteAcceptBody = z.object({ token: z.string().min(3).max(200), customerId: Id.optional() });
export const InviteAcceptResponse = z.object({ kind: z.enum(['client', 'member', 'consultant']), customerId: Id.nullable() });
export type InviteAcceptResponse = z.infer<typeof InviteAcceptResponse>;

/**
 * The consultant invite this login took and hasn't used: the checkout pre-fills the subrogation with it
 * (`PUT /app/subscriptions/:id/subrogation { inviteId }`). The consultant's document is never shown.
 */
export const PendingInviteResponse = z.object({
  consultant: z.object({ inviteId: Id, label: z.string(), anmEntityId: Id, titularLabel: z.string() }).nullable(),
});
export type PendingInviteResponse = z.infer<typeof PendingInviteResponse>;

// --- the team -------------------------------------------------------------------------------------------------------

export const MemberRole = z.enum(['owner', 'member']);
export const MemberItem = z.object({ userId: UserId, name: z.string(), email: z.string(), role: MemberRole, you: z.boolean() });
export type MemberItem = z.infer<typeof MemberItem>;
export const MemberInviteItem = InviteItem.extend({ role: MemberRole });
export const MembersResponse = z.object({ members: z.array(MemberItem), invites: z.array(MemberInviteItem) });
export type MembersResponse = z.infer<typeof MembersResponse>;
export const MemberInviteBody = z.object({ email: Email, role: MemberRole.default('member') });
export const MemberParams = z.object({ userId: UserId });
export const MemberRoleBody = z.object({ role: MemberRole });

export const OkResponse = z.object({ ok: z.literal(true) });
